/**
 * Operational Confidence — derived UI intelligence.
 *
 * Computes a stability confidence score from existing Control Tower data.
 * Pure frontend derivation — no backend changes required.
 */

import type { PulseScoreCore, OperationalAlert, GuestExperienceRisk } from '../staff/services/controlTowerTypes';
import { lowestDimension } from './pressureUtils';

export type ConfidenceLevel = 'HIGH' | 'MEDIUM' | 'FRAGILE' | 'LOW';

export interface OperationalConfidence {
  score: number;
  level: ConfidenceLevel;
}

export type PulseTrend = 'improving' | 'stable' | 'deteriorating';

export function computeOperationalConfidence(
  pulse: PulseScoreCore,
  alerts: OperationalAlert[],
  risks: GuestExperienceRisk[],
  trend: PulseTrend,
): OperationalConfidence {
  let score = 100;

  // Alerts impact
  score -= alerts.length * 10;

  // Guest risk impact
  score -= risks.length * 5;

  // Pulse status impact
  if (pulse.status === 'warning') score -= 10;
  if (pulse.status === 'critical') score -= 20;

  // Weak dimension impact
  const weakest = lowestDimension(pulse.dimensions);
  if (weakest.score < 50) score -= 10;

  // Deteriorating trend impact
  if (trend === 'deteriorating') score -= 5;

  // Clamp 0–100
  score = Math.max(0, Math.min(100, score));

  let level: ConfidenceLevel;
  if (score >= 80) level = 'HIGH';
  else if (score >= 60) level = 'MEDIUM';
  else if (score >= 40) level = 'FRAGILE';
  else level = 'LOW';

  return { score, level };
}
